import { useState } from "react";
import { Layout } from "@/components/Layout";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RotateCcw, Camera, ChevronDown, ChevronRight, Wrench } from "lucide-react";
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InstallationPhotos } from "@/components/installations/InstallationPhotos";
import { 
  INSTALLATION_STATUS_LABELS, 
  INSTALLATION_STATUS_BADGE_COLORS, 
  TASK_TYPE_LABELS 
} from "@/config/constants";
import { getCustomerDisplayName, formatDate } from "@/lib/formatters";
import { InstallationWithRelations, InstallerSummary } from "@/types/installations";

/** Installations-sivun asennus sisältää order.customer ja order.cemetery */
type PageInstallation = InstallationWithRelations & {
  order: {
    order_number: string;
    order_type: string;
    customer: {
      first_name: string | null;
      last_name: string | null;
      company_name: string | null;
      phone: string;
    };
    cemetery: {
      name: string;
      city: string;
    };
  };
  installer?: InstallerSummary | null;
};

export default function Installations() {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const queryClient = useQueryClient();

  const { data: installations, isLoading } = useQuery({
    queryKey: ["all-installations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("installations")
        .select(`
          id,
          status,
          task_type,
          scheduled_date,
          completed_date,
          notes,
          installer_id,
          order:orders (
            order_number,
            order_type,
            customer:customers (
              first_name,
              last_name,
              company_name,
              phone
            ),
            cemetery:cemeteries (
              name,
              city
            )
          )
        `)
        .is("deleted_at", null)
        .order("scheduled_date", { ascending: true, nullsFirst: false });

      if (error) throw error;

      // Batch fetch installer profiles - eliminates N+1 queries
      const installerIds = [...new Set(
        (data as unknown as PageInstallation[])
          .map(inst => inst.installer_id)
          .filter(Boolean)
      )] as string[];

      const installerMap: Record<string, InstallerSummary> = {};
      if (installerIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, first_name, last_name")
          .in("id", installerIds);
        
        profiles?.forEach(profile => {
          installerMap[profile.id] = {
            first_name: profile.first_name,
            last_name: profile.last_name,
          };
        });
      }

      // Merge installer data into installations
      return (data as unknown as PageInstallation[]).map(installation => ({
        ...installation,
        installer: installation.installer_id ? installerMap[installation.installer_id] || null : null,
      }));
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "pending" | "in_progress" | "completed" | "cancelled" }) => {
      const updateData: { status: "pending" | "in_progress" | "completed" | "cancelled"; completed_date?: string } = { status };
      
      if (status === "completed") {
        updateData.completed_date = new Date().toISOString().split("T")[0];
      }

      const { error } = await supabase
        .from("installations")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["all-installations"] });
      toast.success("Asennuksen tila päivitetty");
    },
    onError: (error) => {
      console.error("Error updating installation:", error);
      toast.error("Tilan päivitys epäonnistui");
    },
  });

  const getInstallerName = (installer: InstallerSummary | null | undefined) => {
    if (!installer) return "Ei määritetty";
    return `${installer.first_name || ""} ${installer.last_name || ""}`.trim() || "Ei nimeä";
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Asennukset</h1>
          <p className="text-muted-foreground mt-1">
            {installations?.length || 0} asennusta yhteensä
          </p>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead>Tilaus</TableHead>
                <TableHead>Asiakas</TableHead>
                <TableHead>Hautausmaa</TableHead>
                <TableHead>Asentaja</TableHead>
                <TableHead>Tyyppi</TableHead>
                <TableHead>Suunniteltu pvm</TableHead>
                <TableHead>Tila</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {installations?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-muted-foreground">
                    Ei asennuksia
                  </TableCell>
                </TableRow>
              ) : (
                installations?.map((installation) => (
                  <>
                    <TableRow key={installation.id}>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => toggleRow(installation.id)}
                        >
                          {expandedRows.has(installation.id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-medium">
                        #{installation.order.order_number}
                      </TableCell>
                      <TableCell>
                        {getCustomerDisplayName(installation.order.customer)}
                      </TableCell>
                      <TableCell>
                        {installation.order.cemetery.name}, {installation.order.cemetery.city}
                      </TableCell>
                      <TableCell>
                        {getInstallerName(installation.installer)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">
                            {TASK_TYPE_LABELS[installation.task_type as keyof typeof TASK_TYPE_LABELS] || installation.task_type}
                          </Badge>
                          {installation.order.order_type === "pickup_work" && (
                            <Badge variant="secondary" className="flex items-center gap-1">
                              <RotateCcw className="h-3 w-3" />
                              Palautus
                            </Badge>
                          )}
                          {installation.order.order_type === "field_work" && (
                            <Badge className="flex items-center gap-1 bg-purple-100 text-purple-800 border-purple-300">
                              <Wrench className="h-3 w-3" />
                              Kenttätyö
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {formatDate(installation.scheduled_date) !== "-" 
                          ? formatDate(installation.scheduled_date) 
                          : "Ei määritetty"}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={installation.status}
                          onValueChange={(value: string) =>
                            updateStatusMutation.mutate({ id: installation.id, status: value as "pending" | "in_progress" | "completed" | "cancelled" })
                          }
                          disabled={updateStatusMutation.isPending}
                        >
                          <SelectTrigger className="w-[140px]">
                            <SelectValue>
                              <Badge className={INSTALLATION_STATUS_BADGE_COLORS[installation.status as keyof typeof INSTALLATION_STATUS_BADGE_COLORS] || "bg-gray-500"}>
                                {INSTALLATION_STATUS_LABELS[installation.status as keyof typeof INSTALLATION_STATUS_LABELS] || installation.status}
                              </Badge>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Odottaa</SelectItem>
                            <SelectItem value="in_progress">Käynnissä</SelectItem>
                            <SelectItem value="completed">Valmis</SelectItem>
                            <SelectItem value="cancelled">Peruttu</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                    {expandedRows.has(installation.id) && (
                      <TableRow key={`${installation.id}-photos`}>
                        <TableCell colSpan={8} className="bg-muted/30 p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Camera className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">Kuvadokumentointi</span>
                          </div>
                          <InstallationPhotos installationId={installation.id} readonly />
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
}
